import { getCartMachine } from '../../core/cart';
import { publishGiftboxRemoveEvent } from '../utils/analytics';
import { unassignLineFromGiftBox } from '../utils/cart-line-operations';
/**
 * Remove a gift box from the cart.
 *
 * Only the giftbox product line itself is deleted. All associated child items
 * (apparel and their embroidery personalization children) have their
 * _gift_box_parent_id property cleared via /cart/change.js — they are never
 * deleted. This prevents Shopify's cascade-delete from wiping out embroidery
 * nested lines when their apparel parent is removed.
 *
 * Works from both Preact components (cart drawer) and server-rendered Liquid
 * templates (cart page).
 */ export const removeGiftBox = async (itemKey)=>{
    try {
        var _parentItem_properties;
        // Fetch current cart to resolve the giftbox id and find associated items
        const res = await fetch('/cart.js');
        if (!res.ok) return;
        const cart = await res.json();
        const parentItem = (cart.items || []).find((it)=>it.key === itemKey);
        if (!parentItem) return;
        const parentId = ((_parentItem_properties = parentItem.properties) === null || _parentItem_properties === void 0 ? void 0 : _parentItem_properties._gift_box_id) || parentItem.key;
        const children = (cart.items || []).filter((it)=>{
            var _it_properties;
            return ((_it_properties = it.properties) === null || _it_properties === void 0 ? void 0 : _it_properties._gift_box_parent_id) === parentId && it.key !== itemKey;
        });
        // For each child: merge back into a non-gifted sibling if one exists,
        // otherwise clear _gift_box_parent_id in place.
        for (const child of children){
            try {
                await unassignLineFromGiftBox(child, cart.items || []);
            } catch (err) {
                console.error('Exception while clearing gift box properties', child.key, err);
            }
        }
        // Remove only the giftbox product line via the cart machine
        getCartMachine().changeQuantity({
            items: [
                {
                    lineItemKey: itemKey,
                    quantity: 0
                }
            ]
        });
        const isCartPage = document.body.getAttribute('data-shopify-template') === 'cart';
        // Poll until the giftbox line is gone
        const maxAttempts = 20;
        for(let i = 0; i < maxAttempts; i++){
            await new Promise((r)=>setTimeout(r, 250));
            try {
                const pollRes = await fetch('/cart.js');
                if (!pollRes.ok) continue;
                const pollCart = await pollRes.json();
                const stillPresent = (pollCart.items || []).some((it)=>it.key === itemKey);
                if (!stillPresent) {
                    try {
                        publishGiftboxRemoveEvent(parentItem);
                    } catch  {
                    // ignore analytics errors
                    }
                    if (isCartPage) {
                        window.location.reload();
                        return;
                    }
                    window.dispatchEvent(new CustomEvent('cart-drawer:update', {
                        detail: pollCart
                    }));
                    return;
                }
            } catch (_) {
            // ignore polling errors and retry
            }
        }
        // Fallback: polling timed out
        if (isCartPage) {
            window.location.reload();
            return;
        }
        try {
            const updatedCartRes = await fetch('/cart.js');
            const updatedCart = updatedCartRes.ok ? await updatedCartRes.json() : null;
            window.dispatchEvent(new CustomEvent('cart-drawer:update', {
                detail: updatedCart
            }));
        } catch (_) {
        // ignore
        }
    } catch (err) {
        window.dispatchEvent(new CustomEvent('gift-wrapping:error', {
            detail: {
                message: 'Unexpected error removing giftbox',
                error: err
            }
        }));
    }
};
